<template>
  <v-container fluid grid-list-sm>
    <v-card>
      <v-toolbar flat>
        <v-toolbar-title>
          <span class="font-weight-medium">
            {{ $t('ITEM.EDIT') }} {{ $t('EREPORTER.INVOICES.ITEM') }}
          </span>
        </v-toolbar-title>

        <v-spacer />

        <v-btn dark color="indigo" @click="$router.go(-1)">
          {{ $t('BUTTON.BACK') }}
        </v-btn>
      </v-toolbar>
      <v-divider></v-divider>
    </v-card>
    <v-card class="mt-3" :loading="loading">
      <v-card-subtitle v-if="errorsWithoutForm.length">
        <errors :errors="errorsWithoutForm" />
      </v-card-subtitle>
      <v-form ref="form" v-model="valid">
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <invoice-form
                v-if="Object.keys(invoice).length"
                v-model="invoice"
                :action="$options.action"
                :address="invoice.address"
                :customer="customer"
                :customer-type="invoice.customer_type"
                :errors="errors.getAll()"
                @update-prop="clearErrorByName($event)"
                @change-address="editAddress"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <invoice-line-list
                class="mt-5 pa-5"
                :lines="indexedLines"
                :total-price="totalPrice"
                :sort-by="['default']"
                :type="getListTypeByInvoiceType(invoice.type_id)"
              >
                <template #actions="{ item }">
                  <v-btn
                    class="mr-1"
                    icon
                    text
                    :disabled="!isEditableInvoice"
                    @click="editLine(item)"
                  >
                    <v-icon>
                      mdi-square-edit-outline
                    </v-icon>
                  </v-btn>

                  <v-btn
                    v-if="!item.entity_id"
                    class="mr-1"
                    icon
                    text
                    :disabled="!isEditableInvoice"
                    @click="deleteLine(item)"
                  >
                    <v-icon>
                      mdi-delete
                    </v-icon>
                  </v-btn>
                </template>

                <template v-if="isEditableInvoice" #body-append="{ headers }">
                  <tr>
                    <td :colspan="headers.length - 1"></td>
                    <td class="text-center">
                      <v-btn class="mr-1" icon text @click="createLine">
                        <v-icon>
                          mdi-square-edit-outline
                        </v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </template>
              </invoice-line-list>

              <v-dialog v-model="dialog" persistent max-width="800px">
                <template v-if="actionLine">
                  <component
                    :is="`${actionLine}-invoice-line`"
                    :key="actionLine"
                    :line="line"
                    @cancel="cancelDialog"
                    @update="updateLine"
                    @create="addLine"
                  />
                </template>
              </v-dialog>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-btn
                color="indigo"
                :dark="isEditableInvoice"
                :disabled="!isEditableInvoice"
                @click="createLine"
              >
                <v-icon left>mdi-plus</v-icon>
                {{ $t('ITEM.NEW') }} {{ $t('EREPORTER.INVOICES.LINE') }}
              </v-btn>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-btn
                large
                class="float-right"
                color="primary"
                :disabled="!valid"
                @click="update"
              >
                {{ $t('BUTTON.UPDATE') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-form>
    </v-card>

    <v-dialog v-model="dialogAddress" persistent max-width="800px">
      <template v-if="changeAddress">
        <UpdateAddress
          :customer-id="customer.id"
          :customer-type="invoice.customer_type"
          @cancel="cancelAddress"
          @update="updateAddress"
        />
      </template>
    </v-dialog>

    <confirm-dialog ref="confirm" />
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import errors from '@/modules/_global/mixins/errors';
import i18nRefreshData from '@/modules/_global/mixins/i18nRefreshData';
import InvoiceForm from '../../components/forms/Invoices/InvoiceForm';
import InvoiceLineList from '../../components/Invoices/InvoiceLineList.vue';
import { OPEN } from '../../common/Invoices/statuses';
import { EDIT } from '@/modules/_global/mixins/form-editable';
import { CONTRACT } from '@/modules/ereporter/common/Invoices/types';
import {
  C,
  BASE
} from '@/modules/ereporter/components/Invoices/InvoiceLineList.vue';
export default {
  name: 'InvoicesEditPage',

  action: EDIT,

  components: {
    InvoiceForm,
    InvoiceLineList,
    ConfirmDialog: () => import('@/modules/_global/_components/ConfirmDialog'),
    CreateInvoiceLine: () =>
      import('../../components/Invoices/CreateInvoiceLine'),
    EditInvoiceLine: () => import('../../components/Invoices/EditInvoiceLine'),
    Errors: () => import('@/modules/_global/_components/Errors'),
    UpdateAddress: () => import('../../components/Invoices/UpdateAddress.vue')
  },

  mixins: [errors, i18nRefreshData],

  props: {
    id: {
      type: Number,
      required: true
    }
  },

  data: () => ({
    valid: false,
    invoice: {},
    customer: {},
    dialog: false,
    dialogAddress: false,
    line: null,
    lines: [],
    actionLine: null,
    changeAddress: false
  }),

  computed: {
    ...mapGetters(['loading']),
    ...mapState('ereporter', ['currentInvoice']),

    errorsWithoutForm() {
      return ['unit_price', '_unexpected']
        .reduce((acc, i) => acc.concat(this.errors.get(i)), [])
        .filter(i => i);
    },

    isEditableInvoice() {
      return this.currentInvoice.status_id === OPEN;
    },

    totalPrice() {
      const totalPrice = this.lines.reduce(function(total, item) {
        return total + item.price;
      }, 0);

      return Math.round((totalPrice + Number.EPSILON) * 100) / 100;
    },

    indexedLines() {
      return this.lines.map((item, index) => ({
        _index: index,
        ...item
      }));
    },

    isLastLine() {
      return this.lines.length === 1;
    }
  },

  mounted() {
    this.refreshData();
  },

  methods: {
    ...mapActions('ereporter', [
      'getInvoice',
      'updateInvoice',
      'removeInvoice'
    ]),

    refreshData() {
      this.getInvoice({ invoiceId: this.id }).then(({ data }) => {
        this.invoice = data.data;
        this.customer = data.data.customer;
        this.lines = [...data.data.lines];
      });
    },

    getListTypeByInvoiceType(type) {
      switch (type) {
        case CONTRACT:
          return C;

        default:
          return BASE;
      }
    },

    update() {
      this.updateInvoice({
        invoiceId: this.id,
        invoice: {
          ...this.invoice,
          lines: this.lines
        }
      })
        .then(() => {
          this.$router.push({ name: 'invoices' });
        })
        .catch(({ response }) => {
          this.handleErrorsByResponse(response);
        });
    },

    createLine() {
      this.line = {};
      this.actionLine = 'create';
      this.dialog = true;
    },

    editLine(line) {
      this.line = line;
      this.actionLine = 'edit';
      this.dialog = true;
    },

    cancelDialog() {
      this.dialog = false;
      this.line = null;
      this.actionLine = null;
    },

    addLine(line) {
      this.lines.push(line);
      this.cancelDialog();
    },

    updateLine(line) {
      this.lines.splice(line._index, 1, line);
      this.cancelDialog();
    },

    deleteLine(line) {
      this.$refs.confirm
        .open(
          `${this.$t('ITEM.DELETE')} ${this.$t('EREPORTER.INVOICES.LINE')}?`
        )
        .then(confirm => {
          if (confirm && this.isLastLine) {
            this.deleteInvoice();

            return false;
          }

          return confirm;
        })
        .then(confirm => {
          if (confirm) {
            this.lines.splice(line._index, 1);
          }
        });
    },

    deleteInvoice() {
      this.$refs.confirm
        .open(
          `${this.$t('ITEM.DELETE')} ${this.$t('EREPORTER.INVOICES.ITEM')}?`
        )
        .then(confirm => {
          if (confirm) {
            this.removeInvoice({ invoiceId: this.id })
              .then(() => {
                this.$router.push({ name: 'invoices' });
              })
              .catch(({ response }) => {
                this.handleErrorsByResponse(response);
              });
          }
        });
    },

    editAddress() {
      this.dialogAddress = true;
      this.changeAddress = true;
    },

    updateAddress({ address, addressable_id }) {
      this.dialogAddress = false;
      this.changeAddress = true;
      this.invoice.address = address;
      this.invoice.addressable_id = addressable_id;
    },

    cancelAddress() {
      this.dialogAddress = false;
      this.changeAddress = false;
    }
  }
};
</script>
